/**
 * Conversation Transfer — the Analytics view.
 *
 * The table lists the hand-offs; this shows the shape of them: when they
 * happen, who they come from, who they land on, and which conversations are
 * being passed around rather than handled.
 *
 * The trend is drawn on the report's own `bucket` column — the interval the
 * user chose in the drawer — rather than on a bucketing invented here, so the
 * chart and the table agree on what a period is.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  ArrowLeftRight, ArrowRightLeft, CalendarClock, Clock, Layers, Repeat, Users,
} from 'lucide-react'

import {
  AnalyticsCanvas,
  AttentionList,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  Heatmap,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
  type AttentionItem,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  WEEKDAYS,
  formatCount,
  formatPct,
  hourDayMatrix,
  hourLabel,
  lineDataset,
  lineOptions,
  ratePct,
  tally,
} from '../analytics/viz.js'

type Row = Record<string, unknown>

/** Conversations passed on at least this many times are called out. */
const BOUNCE_ALERT = 3

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim()
}

/** `2026-08-14T11:00:00` → a short, interval-appropriate label. */
function bucketLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function ConversationTransferAnalytics({
  rows,
}: {
  rows: readonly Row[]
}): JSX.Element {
  // ── Trend over the report's own interval buckets ───────────────────────────
  const series = useMemo(() => {
    const counts = new Map<string, number>()
    for (const row of rows) {
      const bucket = text(row['bucket'])
      if (!bucket) continue
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1)
    }
    const labels = [...counts.keys()].sort()
    return { labels, values: labels.map((l) => counts.get(l) ?? 0) }
  }, [rows])

  const trendData = useMemo(
    () => ({
      labels: series.labels,
      datasets: [lineDataset('Transfers', series.values, VIZ.series[0], { fill: true })],
    }),
    [series],
  )

  const busiest = useMemo(() => {
    let best: { label: string; value: number } | null = null
    series.labels.forEach((label, i) => {
      const value = series.values[i] ?? 0
      if (!best || value > best.value) best = { label, value }
    })
    return best as { label: string; value: number } | null
  }, [series])

  const heat = useMemo(() => hourDayMatrix(rows, 'transferredAt'), [rows])

  // ── Bounce analysis ────────────────────────────────────────────────────────
  //
  // The number this report exists for: a conversation transferred once was
  // routed; one transferred five times was passed around.
  const bounce = useMemo(() => {
    const perConversation = new Map<string, { ref: string; count: number; subject: string }>()
    for (const row of rows) {
      const ref = text(row['referenceId'])
      if (!ref) continue
      const entry = perConversation.get(ref) ?? { ref, count: 0, subject: text(row['subject']) }
      entry.count += 1
      perConversation.set(ref, entry)
    }
    const list = [...perConversation.values()].sort((a, b) => b.count - a.count)
    return {
      conversations: list.length,
      repeated: list.filter((c) => c.count > 1).length,
      worst: list.slice(0, 8),
      avgPerConversation: list.length > 0 ? rows.length / list.length : null,
    }
  }, [rows])

  const bounceItems = useMemo(
    (): AttentionItem[] =>
      bounce.worst
        .filter((c) => c.count >= BOUNCE_ALERT)
        .map((c) => ({
          id: c.ref,
          title: c.ref,
          detail: c.subject || 'No subject',
          value: `${c.count}×`,
          tone: c.count >= 5 ? ('critical' as const) : ('warning' as const),
        })),
    [bounce],
  )

  // ── Endpoints ──────────────────────────────────────────────────────────────
  const toItems = (key: string, limit = 8): BarListItem[] =>
    tally(rows, key, { limit, emptyLabel: 'Not recorded' }).map((slice) => ({
      label: slice.label,
      value: slice.value,
      meta: formatPct(ratePct(slice.value, rows.length), 1),
    }))

  const fromAgents = useMemo(() => toItems('fromAgent'), [rows])
  const toAgents = useMemo(() => toItems('toAgent'), [rows])
  const types = useMemo(() => toItems('transferType'), [rows])
  const reasons = useMemo(() => toItems('reason'), [rows])
  const teams = useMemo(() => toItems('toTeam'), [rows])
  const locations = useMemo(() => toItems('locationName'), [rows])

  const kinds = useMemo(() => {
    const counts = { Agent: 0, Team: 0, Queue: 0 }
    for (const row of rows) {
      const kind = text(row['toKind'])
      if (kind === 'Agent' || kind === 'Team' || kind === 'Queue') counts[kind] += 1
    }
    return counts
  }, [rows])

  if (rows.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            No conversation was transferred in this window. Widen the range, or clear the location
            filters, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const peak = heat.peak

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="Transfers in scope"
        value={formatCount(rows.length)}
        caption={`Across ${formatCount(bounce.conversations)} conversation${bounce.conversations === 1 ? '' : 's'} in the selected window`}
        spark={series.values}
        Icon={ArrowRightLeft}
        facts={[
          { label: 'Conversations moved', value: formatCount(bounce.conversations) },
          {
            label: 'Transferred more than once',
            value: formatCount(bounce.repeated),
            tone: bounce.repeated > 0 ? 'warning' : 'good',
          },
          {
            label: 'Avg per conversation',
            value: bounce.avgPerConversation === null ? '—' : bounce.avgPerConversation.toFixed(2),
          },
          { label: 'To an agent', value: formatPct(ratePct(kinds.Agent, rows.length), 1) },
        ]}
      />

      <KpiRow>
        <StatTile label="To an agent" value={formatCount(kinds.Agent)} hint="Named person" />
        <StatTile label="To a team" value={formatCount(kinds.Team)} hint="Shared ownership" />
        <StatTile label="To a queue" value={formatCount(kinds.Queue)} hint="Back to the pool" />
        <StatTile
          label="Repeatedly moved"
          value={formatCount(bounce.repeated)}
          tone={bounce.repeated > 0 ? 'warning' : 'good'}
          hint={`${formatPct(ratePct(bounce.repeated, bounce.conversations), 1)} of conversations`}
        />
        <StatTile
          label="Distinct senders"
          value={formatCount(new Set(rows.map((r) => text(r['fromAgent'])).filter(Boolean)).size)}
        />
        <StatTile
          label="Distinct receivers"
          value={formatCount(new Set(rows.map((r) => text(r['toAgent'])).filter(Boolean)).size)}
        />
      </KpiRow>

      {/* ── When ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${series.labels.length} interval${series.labels.length === 1 ? '' : 's'}`}>
          When hand-offs happen
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Transfers per interval"
            subtitle="On the interval chosen in the filters"
            Icon={CalendarClock}
            wide
            height={280}
            footnote={
              busiest
                ? `Busiest interval: ${bucketLabel(busiest.label)} with ${formatCount(busiest.value)} transfers.`
                : undefined
            }
          >
            {series.labels.length === 0 ? (
              <CardEmpty message="No transfer in this result set carries an interval." />
            ) : (
              <Chart
                type="line"
                data={trendData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="When transfers happen"
            subtitle="Weekday by hour of day, in your local time"
            Icon={Clock}
            wide
            height={280}
            footnote={
              peak
                ? `Peak hour: ${WEEKDAYS[peak.day]} at ${hourLabel(peak.hour)} — ${formatCount(peak.value)} transfers.`
                : undefined
            }
          >
            <Heatmap matrix={heat.matrix} max={heat.max} />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Who ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Who hands off to whom</SectionLabel>
        <CardGrid>
          <ChartCard title="Transferred from" subtitle="Agents passing conversations on" Icon={ArrowLeftRight}>
            <BarList items={fromAgents} format={formatCount} emptyMessage="No sender recorded." />
          </ChartCard>
          <ChartCard title="Transferred to" subtitle="Agents receiving conversations" Icon={Users}>
            <BarList items={toAgents} format={formatCount} emptyMessage="No receiver recorded." />
          </ChartCard>
          <ChartCard title="Receiving teams" subtitle="Where shared ownership lands" Icon={Users}>
            <BarList items={teams} format={formatCount} emptyMessage="No team transfers in this window." />
          </ChartCard>
          <ChartCard title="Destination type" subtitle="Agent, team or back to a queue" height={220}>
            <Meter
              segments={[
                { label: 'Agent', value: kinds.Agent, color: VIZ.series[0] },
                { label: 'Team', value: kinds.Team, color: VIZ.series[2] },
                { label: 'Queue', value: kinds.Queue, color: VIZ.series[1] },
              ]}
              emptyMessage="No destination recorded on these transfers."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Why ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Why, and what keeps moving</SectionLabel>
        <CardGrid>
          <ChartCard title="Transfer type" subtitle="As recorded on the hand-off" Icon={Layers}>
            <BarList items={types} format={formatCount} emptyMessage="No transfer type recorded." />
          </ChartCard>
          <ChartCard title="Reasons" subtitle="Tail folded into “Other”" Icon={Layers}>
            <BarList items={reasons} format={formatCount} emptyMessage="No reason recorded." />
          </ChartCard>
          <ChartCard
            title="Passed around"
            subtitle={`Conversations transferred ${BOUNCE_ALERT}+ times`}
            Icon={Repeat}
          >
            <AttentionList
              items={bounceItems}
              emptyMessage={`No conversation was transferred ${BOUNCE_ALERT} or more times.`}
            />
          </ChartCard>
          <ChartCard title="By location" subtitle="Where the transferred conversations sit" Icon={Layers}>
            <BarList items={locations} format={formatCount} emptyMessage="No location recorded." />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}
